/**
 * Copyright 2019-2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { use, useEffect, useState, useRef, useMemo } from 'react';
import Script from 'next/script';
import root from 'window-or-global';
import Head from 'next/head';
import cx from 'classnames';
import { Grid } from '../Grid';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import config from '../../config/nonSEOData';
import homePageMeta from '../../config/homePageMeta';
import { routePaths } from '../../constants/routePaths';
import { Header } from '@/features/header/components/Header';
import { HeaderSecondary } from '@/features/header/components/HeaderSecondary';
import { useHeaderData, getHeaderDataKey } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { useQueryClient } from '@tanstack/react-query';
import { ChatSlider, useChatAgentAvailabilityCheck, ProactiveChat } from '@/features/proactiveChat';
import { getPathnameFromAsPath, parseUrl } from '../../utils/urlHelpers';
import { getSHA256 } from '../../utils/getSHA256';
import ContentContainer from '../../components/AZCustomComponent/ContentContainer';
import AZSnackBar from '../../components/AZCustomComponent/AZSnackBar';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { countryCodes, defaultStoreUS } from '@/constants/locale';
import usePrevious from '../../utils/usePrevious';
import { isRouteProtected } from '../../protectedRoutes';
import azCommonStyles from '../../theme/globals.module.scss';
import SmartAppBanner from '../AZCustomComponent/SmartAppBanner';
import { localStorage } from '../../utils/localStorage';
import { sessionStorage } from '../../utils/sessionStorage';
import styles from './styles.module.scss';
import { useAlerts } from '@/hooks/useAlerts';
import { useUtagReady } from '@/utils/analytics/useUtagReady';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { analyticsCookie } from '@/constants/analytics/analyticsCookie';
import { analyticsGlobalVariables } from '@/utils/analytics/dataLayer/analyticsGlobalVariables';
import { Utag } from '@/components/Utag/Utag';
import { UtagSync } from '@/components/Utag/UtagSync';
import { JScrambler } from '@/components/JScrambler/JScrambler';
import { getDecodedCookiesSync, setCookie, setCookieSync } from '@/utils/cookie';
import { Footer } from '@/features/footer/components/Footer';
import { FooterSecondary } from '@/features/footer/components/FooterSecondary';
import { FeatureFlag, useFeatureFlag, useGlobalConfig } from '@/features/globalConfig';
import { getStoredYmmeActionSource } from '@/features/ymme/utils/ymmeActionSourceHelper';
import { getStoredInteractionLocation } from '@/features/ymme/utils/ymmeLocationHelper';
import { ContactUsModal, NeedHelpTab } from '@/features/contactUs';
import { useGeolocation } from '@/hooks/useGeoLocation';
import { throttle } from '@/utils/throttle';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAppState } from '@/stores/AppState';
import { useAppData } from '@/hooks/useAppData';
import { useIsMobileQRExperience } from '@/hooks/useIsMobileQRExperience';
import { ResetPasswordMessageV2 } from '@/features/resetPassword/components/ResetPasswordMessageV2';
import { useSignInFlowType } from '../SignInFlowTypeContext';
import dynamic from 'next/dynamic';
import { API_URLS } from '@/constants/apiUrls';
import { useBanner90DayContext } from '../Banner90DayContext';
import { useModalTCPromptContext } from '../ModalTCPromptContext';
import { getCustomScripts } from '@/utils/getCustomScripts';
import { useSearchStoreMutation } from '@/features/fulfillment/api/getSearchStore';
import { useSetPreferredStoreMutation } from '@/features/fulfillment/api/postPreferredStore';
import logger from '@/utils/logger';
import { useResetSignInNotifications } from '@/hooks/useResetSignInNotifications';
import { trackAddVehicle } from '@/utils/trackAddVehicle';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { SnackBarContext } from '../SnackBarContext';
import { EmailSideTab } from '@/features/emailTab';
import { OneTrustCookieBanner, useOneTrustContext } from '@az/starc-ui';
import { CMSExperienceInjectorV2 } from '@/features/experienceInjectorV2/components/CMSExperienceInjectorV2';
import { oneTrustLabelKeys } from '@/constants/oneTrustLabels';
import { ForteTargeting } from '@/features/forte/components/ForteTargeting';
import { getFormattedDateRange } from '@/features/orderHistory/utils/getFormattedDateRange';
import { useBuyAgainPurchases } from '@/features/orderHistory/api/getBuyAgainPurchases';
import { usePageType } from '@/hooks/usePageType';
import { pageTypes } from '@/constants/page';
import { useRouter } from 'next/navigation';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';

const LazyLCPMeasurements = dynamic(
  () =>
    import('@/features/performance/components/LCPMeasurements').then((mod) => mod.LCPMeasurements),
  { ssr: false },
);

const LazyBanner90Day = dynamic(
  () => import('@/components/Banner90Day').then((mod) => mod.Banner90Day),
  { ssr: false },
);

const LazyRewardsPilotProgramBanner = dynamic(
  () =>
    import('@/components/RewardsPilotProgramBanner').then((mod) => mod.RewardsPilotProgramBanner),
  { ssr: false },
);

const trackChangeVehicleSuccess = (vehicleData: {
  userVehicleCount: string;
  vehicleYear: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleEngine: string;
  vehicleId: string;
  interactionLocation: string;
}) => {
  const { pageName = '', pageType = '', siteSection = '' } = root.utag_data || {};
  clickTrack({
    eventType: 'changeVehicle',
    pageName: pageName,
    pageType: pageType,
    siteSection: siteSection,
    userVehicleCount: vehicleData.userVehicleCount,
    vehicleYear: vehicleData.vehicleYear,
    vehicleMake: vehicleData.vehicleMake,
    vehicleModel: vehicleData.vehicleModel,
    vehicleEngine: vehicleData.vehicleEngine,
    vehicleId: vehicleData.vehicleId,
    vehicle_interaction_location: vehicleData.interactionLocation,
  });
};

type AppProps = {
  children: React.ReactNode;
};

const labelMap = {
  lblShoppingFor: 'label_YMME_Body_YouAreNowShoppingFor',
  lblYouAreNowSignedIn: 'label_you_are_now_signed_in',
  lblScrollToSeeItemsThatFit: 'label_scroll_to_see_items_that_fit',
  // OneTrust Cookie Banner Labels
  lblCookieBannerAcceptAll: oneTrustLabelKeys.acceptAllButtonText,
  lblCookieBannerRejectAll: oneTrustLabelKeys.rejectAllButtonText,
  lblCookieBannerManagePreferences: oneTrustLabelKeys.managePreferencesLinkText,
  lblCookieBannerHeader: oneTrustLabelKeys.bannerHeaderText,
  lblCookieBannerMainMessage: oneTrustLabelKeys.bannerMainMessageText,
  lblCookieBannerBoldMessage: oneTrustLabelKeys.bannerBoldMessageText,
  lblCookieBannerLearnMore: oneTrustLabelKeys.learnMoreText,
  lblCookieBannerPrivacyPolicy: oneTrustLabelKeys.privacyPolicyText,
  lblCookieBannerPrivacyPolicyUrl: oneTrustLabelKeys.privacyPolicyUrl,
  lblCookieBannerTermsAndConditions: oneTrustLabelKeys.termsAndConditionsText,
  lblCookieBannerTermsAndConditionsUrl: oneTrustLabelKeys.termsAndConditionsUrl,
  lblCookieBannerClickBelow: oneTrustLabelKeys.clickBelowText,
  lblCookieBannerClickHere: oneTrustLabelKeys.clickHereText,
  lblCookieBannerByUsing: oneTrustLabelKeys.byUsingText,
  lblCookieBannerOr: oneTrustLabelKeys.orText,
  lblCookieBannerAnd: oneTrustLabelKeys.andText,
  lblCookieBannerCookies: oneTrustLabelKeys.cookiesText,
  lblCookieBannerPeriod: oneTrustLabelKeys.dotText,
};

const App = ({ children }: AppProps) => {
  const {
    lblYouAreNowSignedIn,
    lblScrollToSeeItemsThatFit,
    lblShoppingFor,
    lblCookieBannerAcceptAll,
    lblCookieBannerRejectAll,
    lblCookieBannerManagePreferences,
    lblCookieBannerHeader,
    lblCookieBannerMainMessage,
    lblCookieBannerBoldMessage,
    lblCookieBannerLearnMore,
    lblCookieBannerPrivacyPolicy,
    lblCookieBannerPrivacyPolicyUrl,
    lblCookieBannerTermsAndConditions,
    lblCookieBannerTermsAndConditionsUrl,
    lblCookieBannerClickBelow,
    lblCookieBannerClickHere,
    lblCookieBannerByUsing,
    lblCookieBannerOr,
    lblCookieBannerAnd,
    lblCookieBannerCookies,
    lblCookieBannerPeriod,
  } = useLabels(labelMap);
  const dispatch = useDispatch();
  const locale = useLocale();
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const { isMobileApp } = useAppState().state;
  const kiboEnabled = useFeatureFlag('KIBO_ENABLED') === 'true';
  const kiboScriptUrl = useFeatureFlag('KIBO_SCRIPT_URL');
  const { setPageSpecificData } = usePageSpecificData();
  const { data: globalConfig } = useGlobalConfig();
  const customScripts = useMemo(() => getCustomScripts(globalConfig), [globalConfig]);

  const oneTrustDomainScriptUrl = useFeatureFlag('ONE_TRUST_DOMAIN_SCRIPT_URL');
  const oneTrustEnabled = useFeatureFlag('ONE_TRUST_ENABLED') === 'true';
  const oneTrustScriptUrl = useFeatureFlag('ONE_TRUST_SCRIPT_URL');
  const oneTrustEnableNewBanner = useFeatureFlag('ONE_TRUST_ENABLE_NEW_BANNER') === 'true';

  const checkoutVersionEnableV3_us = useFeatureFlag('CHECKOUT_V3_ENABLED_US') === 'true';
  const checkoutVersionEnableV3_mx = useFeatureFlag('CHECKOUT_V3_ENABLED_MX') === 'true';
  const googleSiteVerificationKey = useFeatureFlag('GOOGLE_SITE_VERIFICATION_KEY');
  const isAppFlyerEnabled = useFeatureFlag('APPS_FLYER_ENABLED') === 'true';
  const isSmartAppBannerEnabled = useFeatureFlag('SMART_APP_BANNER_ENABLED') === 'true';
  const performanceLCPMeasurementsEnabled =
    useFeatureFlag('PERFORMANCE_LCP_MEASUREMENTS_ENABLED') === 'true';
  const emailOptInAlertEnabled = useFeatureFlag('EMAIL_OPTIN_ALERT_ENABLED') === 'true';

  const { appData, setChatAgentStatus, setFetchingLocationStatus } = useAppData();
  const { deviceType, userAuthenticated } = appData;
  const { data: profileData } = useMyProfile();

  const canShowToastNotification = localStorage.getItem('canShowToastNotification');

  const [showBannerAnimation, setShowBannerAnimation] = useState(false);
  const bannerDelayTimeout = useFeatureFlag('ONE_TRUST_BANNER_DELAY') || '2000'; // 2 seconds default

  useEffect(() => {
    if (!appData.userAuthenticated) {
      void setCookieSync('loginInteractionMethod', '');
    }
  }, [appData.userAuthenticated]);

  const {
    data: headerData,
    refetch: refetchHeaderData,
    dataUpdatedAt: headerDataUpdateAt,
  } = useHeaderData();
  const acesFlags = useAcesFlags();
  const queryClient = useQueryClient();
  const { triggerAlert } = useAlerts();
  const lastInvalidatedVehicleFlow = useRef<string | null>(null);
  const isAnonymousUser = headerData?.myAccountMap?.anonymous !== 'false';
  const rememberMe = headerData?.myAccountMap?.autoLogin === 'true';
  const cartData = headerData?.analyticsData;
  const vehicleName = headerData?.vehicleMap?.vehicleName || '';
  const preferredVehicleData = getPreferredVehicle(headerData);
  const vehicleId = preferredVehicleData?.catalogVehicleId;
  const vehicleCount = headerData?.vehicleMap?.vehicleCount;
  const prevVehicleCount = usePrevious(vehicleCount);
  const { isUtagReady } = useUtagReady();
  const prevAnonymousStatus = usePrevious(headerData?.myAccountMap?.anonymous);
  const storeID = headerData?.storeNumber;
  const isABot = deviceType === 'bot';
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [shouldRerenderToTrackAddVehicle, setShouldRerenderToTrackAddVehicle] = useState(false);
  const [isContactUsModalOpen, setIsContactUsModalOpen] = useState(false);
  const isMobileQRExperience = useIsMobileQRExperience();
  const { openBanner: isBanner90Open } = useBanner90DayContext();
  const { modalOpen: isRewardsPilotProgramBannerOpen } = useModalTCPromptContext();
  const openSnackBar = (message: string) => {
    setSnackBarMessage(message);
    setShowSnackBar(true);
  };
  const { mutateAsync: searchStore } = useSearchStoreMutation();
  const { mutateAsync: setStore } = useSetPreferredStoreMutation();
  const justMounted = useRef(true);
  const prevVehicleId = usePrevious(vehicleId);
  const prevSessionVehicleId = sessionStorage.getItem('previousPreferredVehicleId');
  const prevHeaderDataUpdateAt = usePrevious(headerDataUpdateAt);
  const [isResetPasswordToastNotificationOpen, setIsResetPasswordToastNotificationOpen] =
    useState(false);
  const { flowType, isSignInModalOpened } = useSignInFlowType();

  const { showSnackBar: openGlobalSnackBar } = use(SnackBarContext);

  /**  CONTACT US MODAL AND CHAT SECTION */
  const contactUsModalEnabled = useFeatureFlag('CONTACT_US_MODAL_ENABLED') === 'true';
  const isV2ChatAgentAvailable = useFeatureFlag('CHECK_AGENT_AVAILABILITY_V2') === 'true';
  const chatAvailabilityCallEnabled =
    useFeatureFlag('GENESYS_AVAILABILITY_CALL_ENABLED') === 'true' && !isABot;
  const chatMobileEnabled = useFeatureFlag('CHAT_ON_MOBILE_ENABLED') === 'true';
  const matchesMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));

  // This is the old behavior to check chat agent availability wich makes API calls every X time  (V1)
  // TODO: delete in (CB-5641)
  useChatAgentAvailabilityCheck();

  const isMxSite = useLocale() === countryCodes.mx;

  const { NEW_CHAT_AVAILABLE_ENDPOINT_FALLBACK } = API_URLS;

  const NEW_CHAT_AVAILABLE_ENDPOINT =
    useFeatureFlag('NEW_CHAT_AVAILABLE_ENDPOINT') ?? NEW_CHAT_AVAILABLE_ENDPOINT_FALLBACK;
  const isChatQueueEnabled = useFeatureFlag('NEW_CHAT_QUEUE_ENABLED') === 'true';

  const mainContentFocus = <div id="main-content" tabIndex={-1} className="sr-only" />;

  const openContactUsModal = () => {
    setIsContactUsModalOpen(true);

    // This is the new behavior to check chat agent availability wich makes API calls on click of the contact us tab (V2)
    if (isV2ChatAgentAvailable && !isMxSite && chatAvailabilityCallEnabled) {
      if (matchesMobile && !chatMobileEnabled) {
        return;
      }
      const throttledCheck = throttle(() => {
        void setChatAgentStatus({ isChatQueueEnabled, NEW_CHAT_AVAILABLE_ENDPOINT });
      }, 3000);
      throttledCheck();
    }
  };
  /**  END CONTACT US MODAL AND CHAT SECTION */

  const coordinates = useGeolocation();

  const preferredStoreFromURL = useRef<string>('');

  if (
    legacyRouter.asPath.includes('spps.s') &&
    !legacyRouter.asPath.includes('new_store_set=true')
  ) {
    const urlParams = new URLSearchParams(legacyRouter.asPath.split('?')[1]);
    preferredStoreFromURL.current = urlParams.get('spps.s') || '';
  }

  const getVehicleAnalyticsData = (isAddByURL?: boolean) => {
    const vehicleData = headerData?.vehicleMap || {
      vehicleCount: '',
      make: '',
      model: '',
      engine: '',
      catalogVehicleId: '',
      year: '',
      makeName: '',
      modelName: '',
      engineName: '',
    };

    return {
      userVehicleCount: vehicleData.vehicleCount,
      vehicleMake: vehicleData.makeName ?? vehicleData.make ?? '',
      vehicleModel: vehicleData.modelName ?? vehicleData.model ?? '',
      vehicleEngine: vehicleData.engineName ?? vehicleData.engine ?? '',
      vehicleId: vehicleData.catalogVehicleId,
      vehicleYear: vehicleData.year,
      interactionLocation: isAddByURL ? 'URL' : getStoredInteractionLocation(),
      actionSource: getStoredYmmeActionSource(),
    };
  };

  const hashUserName = (userName: string) => {
    const hashedEId = getSHA256(userName.toLowerCase());
    void hashedEId.then((hash) => {
      if (root.utag_data) {
        root.utag_data.hashedEId = hash;
      }

      void setCookie(analyticsCookie.cookieHashedEId, hash);
    });
  };

  /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access */
  const isAlertBoxClosed: boolean = (() => {
    try {
      return (root as any)?.OneTrust?.IsAlertBoxClosed?.() ?? true;
    } catch (error) {
      logger.error({
        message: 'Error accessing OneTrust API',
        meta: { error },
      });
      return true;
    }
  })();
  const isOneTrustBannerToDisplay: boolean = (() => {
    try {
      return (root as any)?.OneTrust?.GetDomainData?.().ShowAlertNotice ?? true;
    } catch (error) {
      logger.error({
        message: 'Error accessing OneTrust API',
        meta: { error },
      });
      return false;
    }
  })();

  /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access */

  // Validate vehicle type matches current ACES flow
  useEffect(() => {
    if (!preferredVehicleData?.vehicleName) {
      return;
    }

    const hasAcesAttributes = !!preferredVehicleData.acesVehicleAttributes;

    // Detect mismatch between vehicle type and current flow
    const isMismatch =
      (acesFlags.acesEnabled && !hasAcesAttributes) || // ACES enabled but Parts-Pro vehicle
      (!acesFlags.acesEnabled && hasAcesAttributes); // ACES disabled but ACES vehicle

    if (!isMismatch) {
      lastInvalidatedVehicleFlow.current = null;
      return;
    }

    const mismatchKey = `${
      preferredVehicleData?.catalogVehicleId ?? preferredVehicleData?.vehicleName ?? ''
    }::${acesFlags.acesEnabled ? 'aces' : 'parts-pro'}`;

    if (lastInvalidatedVehicleFlow.current === mismatchKey) {
      return;
    }

    lastInvalidatedVehicleFlow.current = mismatchKey;
    void queryClient.invalidateQueries({ queryKey: getHeaderDataKey() });
  }, [
    preferredVehicleData?.catalogVehicleId,
    preferredVehicleData?.vehicleName,
    acesFlags.acesEnabled,
    queryClient,
    preferredVehicleData?.acesVehicleAttributes,
  ]);

  useEffect(() => {
    const handleDocumentReady = () => {
      const timer = setTimeout(
        () => {
          setShowBannerAnimation(!isAlertBoxClosed && isOneTrustBannerToDisplay);
        },
        parseInt(bannerDelayTimeout, 10),
      );

      return () => clearTimeout(timer);
    };

    if (document.readyState === 'complete') {
      return handleDocumentReady();
    } else {
      const cleanup = handleDocumentReady();
      window.addEventListener('load', handleDocumentReady);
      return () => {
        window.removeEventListener('load', handleDocumentReady);
        cleanup?.();
      };
    }
  }, [bannerDelayTimeout, isAlertBoxClosed, isOneTrustBannerToDisplay]);

  useEffect(() => {
    if (locale === countryCodes.ptBr) {
      return;
    }
    const hideSignInAlert = () => {
      sessionStorage.setItem('signInAlertShown', 'true');
      return null;
    };

    window.addEventListener('pagehide', hideSignInAlert);

    return () => {
      window.removeEventListener('pagehide', hideSignInAlert);
    };
  }, [locale]);

  useEffect(() => {
    if (locale === countryCodes.ptBr) {
      return;
    }
    const shouldDisplayLoginWelcome =
      localStorage.getItem('shouldDisplayLoginMsgOnReload') === 'true' &&
      sessionStorage.getItem('isNewAccount') !== 'true' &&
      flowType !== 'manualCreateAccount';

    //Controls the sign in snack bar when site is reloaded
    if (
      shouldDisplayLoginWelcome &&
      appData.userAuthenticated &&
      !isMobileApp &&
      legacyRouter.pathname !== routePaths.signIn
    ) {
      openGlobalSnackBar(lblYouAreNowSignedIn);
      sessionStorage.setItem('signInAlertShown', 'true');
      sessionStorage.setItem('signInAlertDisabled', 'true');
    }
    /* If it is cart page, we don't want to remove the variables yet.
      We want to remove them only after the automatic refresh that occurs after login
    */
    if (legacyRouter.pathname !== routePaths.cart && legacyRouter.pathname !== routePaths.signIn) {
      localStorage.removeItem('shouldDisplayLoginMsgOnReload');
    }
  }, [
    flowType,
    lblYouAreNowSignedIn,
    appData.userAuthenticated,
    legacyRouter.pathname,
    isMobileApp,
    locale,
    openGlobalSnackBar,
  ]);

  const { showBanner } = useOneTrustContext();

  useEffect(() => {
    if (typeof root !== 'undefined') {
      root.onerror = (err) => {
        if (root?.utag_data) {
          root.utag_data.jsErrorMsg = err;
        }

        return true;
      };
    }
    /**Analytics End*/
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (locale === countryCodes.ptBr) {
      return;
    }
    const cookies = getDecodedCookiesSync([
      'loginInteractionMethod',
      analyticsCookie.cookieisSignedIn,
    ]);

    const userName = profileData?.email ?? '';
    const isResetPwFlow = ['resetPassword'].includes(
      localStorage.getItem('loggedInFrom') as string,
    );
    const isManualCreateAccountFlow =
      flowType === 'manualCreateAccount' || sessionStorage.getItem('isNewAccount') === 'true';
    const delaySignInSnackBar = localStorage.getItem('delaySignInSnackBar');

    const handleSnackBar = () => {
      if (localStorage.getItem('delaySignInSnackBar') === 'abort') {
        return;
      }
      openGlobalSnackBar(lblYouAreNowSignedIn);
      sessionStorage.setItem('signInAlertShown', 'true');
    };

    const shouldShowSnackBar = () => {
      const isSignInAlertDisabled = sessionStorage.getItem('signInAlertDisabled') === 'true';
      switch (true) {
        case isMobileApp:
          return false;
        case isManualCreateAccountFlow:
          return false;
        case isResetPwFlow:
          return false;
        case isSignInAlertDisabled:
          return false;
        default:
          return true;
      }
    };

    if (
      appData.userAuthenticated &&
      legacyRouter.asPath !== routePaths.signIn &&
      legacyRouter.pathname !== routePaths.signIn &&
      cookies.get(analyticsCookie.cookieisSignedIn) !== 'true'
    ) {
      //Controls the sign in snack bar when the site is not reloaded
      if (localStorage.getItem('shouldDisplayLoginMsgOnReload') !== 'true') {
        if (shouldShowSnackBar() && delaySignInSnackBar === null) {
          handleSnackBar();
        }
      }

      if (shouldShowSnackBar() && delaySignInSnackBar === 'true') {
        root.document.addEventListener('delayedSignInSnackBar', handleSnackBar);
      }

      const { loginFailureFlag } = root.utag_data || {};
      if (userName) {
        hashUserName(userName);
      }

      void setCookie(analyticsCookie.cookieisSignedIn, 'true');

      if (loginFailureFlag) {
        delete root.utag_data?.loginFailureFlag;
      }
    }

    return () => {
      if (localStorage.getItem('delaySignInSnackBar') === 'true') {
        // Continue listening for delayedSignInSnackBar
        return;
      }
      document.removeEventListener('delayedSignInSnackBar', handleSnackBar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appData.userAuthenticated, legacyRouter.asPath, profileData?.email]);

  const userName = headerData?.myAccountMap?.mf_homepage_header_username || '';

  useEffect(() => {
    if (
      headerData?.myAccountMap?.anonymous !== 'false' &&
      (!headerData?.storeNumber || headerData.storeNumber === defaultStoreUS) &&
      !isMobileApp &&
      !legacyRouter.asPath.includes('spps.s') &&
      !legacyRouter.asPath.includes('/checkout')
    ) {
      if (!isABot && coordinates !== null) {
        setFetchingLocationStatus(true);
        const { latitude, longitude } = coordinates;

        const setStoreForApp = async () => {
          let storeNumber = locale !== countryCodes.ptBr ? defaultStoreUS : '';
          let storeCountryCode = '';

          try {
            const stores = await searchStore({
              location: [`${latitude}`, `${longitude}`],
              country: null,
              countryCode: null,
              includeFilters: false,
            });
            const firstOpenStore = stores.response.locations.find(
              (store) => !store?.closed?.isClosed,
            );

            if (firstOpenStore) {
              storeNumber = firstOpenStore.id;
              storeCountryCode = firstOpenStore.countryCode;
            }

            const isOutOfBrazilStore = locale === countryCodes.ptBr && storeCountryCode !== 'BR';
            if (isOutOfBrazilStore) {
              storeNumber = '';
            }
          } catch (err) {
            logger.error({
              message:
                err && typeof err === 'object' && 'message' in err ? (err.message as string) : '',
            });
          } finally {
            if (storeNumber) {
              await setStore({ storeNumber });
              void refetchHeaderData();
            }

            setFetchingLocationStatus(false);
          }
        };

        void setStoreForApp();
      }
    }
  }, [
    locale,
    headerData?.myAccountMap?.anonymous,
    isMobileApp,
    dispatch,
    headerData?.storeNumber,
    isABot,
    legacyRouter.asPath,
    refetchHeaderData,
    coordinates,
    setStore,
    searchStore,
    setFetchingLocationStatus,
  ]);

  useEffect(() => {
    if (locale === countryCodes.ptBr) {
      return;
    }

    async function setStoreFromURL({ onSuccess }: { onSuccess?: () => void }) {
      try {
        await setStore({ storeNumber: preferredStoreFromURL.current });
        onSuccess?.();
      } catch {
        // ignore setStore errors
      }
      void refetchHeaderData();
    }
    if (storeID === preferredStoreFromURL.current) {
      setPageSpecificData({
        ignoreNextPageViewCall: false,
      });
      preferredStoreFromURL.current = '';
    } else if (preferredStoreFromURL.current) {
      void setStoreFromURL({ onSuccess: () => triggerAlert('storeLocationPrompt') });
      const [baseUrl, queryParams] = legacyRouter?.asPath?.split('?') ?? [];
      const paramsList = queryParams ? queryParams.split('&') : []; //Remove the store param from the list after setting the store.

      const taggedParamList = paramsList.concat('new_store_set=true');
      setPageSpecificData({
        ignoreNextPageViewCall: true,
      });

      const urlParams = new URLSearchParams(legacyRouter.asPath.split('?')[1]);
      taggedParamList.forEach((param) => {
        const [key, value] = param.split('=');
        urlParams.set(key, value);
      });

      urlParams.set('new_store_set', 'true');

      const url = `${baseUrl}?${urlParams.toString()}`;

      if (legacyRouter.push) {
        void legacyRouter.push(url, undefined, {
          shallow: true,
        });
      } else {
        router.push(url);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredStoreFromURL.current, storeID, refetchHeaderData]);

  useEffect(() => {
    void setCookieSync(
      analyticsCookie.cookieuserVehicleCount,
      String(headerData?.vehicleMap?.vehicleCount ?? ''),
    );
  }, [headerData, locale]);

  useEffect(() => {
    const isAddByURL =
      (!!legacyRouter.query.vehicleId &&
        legacyRouter.query.vehicleId === vehicleId &&
        vehicleId !== prevSessionVehicleId) ||
      !!legacyRouter.query.makeName;

    if (
      vehicleCount &&
      isUtagReady &&
      !isSignInModalOpened &&
      legacyRouter.asPath !== routePaths.checkout &&
      legacyRouter.asPath !== routePaths.signIn &&
      ((!isAddByURL && vehicleCount && vehicleId !== prevSessionVehicleId && !prevVehicleCount) ||
        (prevVehicleCount && (prevVehicleCount < vehicleCount || shouldRerenderToTrackAddVehicle)))
    ) {
      if (isUtagReady) {
        trackAddVehicle({ isAddByURL, headerData, acesFlags });
        setShouldRerenderToTrackAddVehicle(false);
      } else {
        setShouldRerenderToTrackAddVehicle(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUtagReady, shouldRerenderToTrackAddVehicle, vehicleCount, prevVehicleCount]);

  useEffect(() => {
    const isAddByURL =
      (!!legacyRouter.query.vehicleId &&
        legacyRouter.query.vehicleId === vehicleId &&
        vehicleId !== prevVehicleId) ||
      !!legacyRouter.query.makeName;
    if (
      !!vehicleId &&
      !!prevVehicleId &&
      prevVehicleCount === vehicleCount &&
      vehicleId !== prevVehicleId &&
      isUtagReady
    ) {
      trackChangeVehicleSuccess(getVehicleAnalyticsData(isAddByURL));
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUtagReady, vehicleId]);

  useEffect(() => {
    if (locale === countryCodes.ptBr) {
      return;
    }
    if (cartData) {
      const cap = (v: unknown) => {
        const s = typeof v === 'string' ? v : String(v);
        return s.length > 3800 ? s.slice(0, 3800) : s;
      };
      void setCookieSync('cartProductPartIds', cap(cartData.cartProductPartIds));
      void setCookieSync('cartProductSkus', cap(cartData.cartProductSkus));
      void setCookieSync('cartProductTitles', cap(cartData.cartProductTitles));
      void setCookieSync('cartProductVendors', cap(cartData.cartProductVendors));
      void setCookieSync('cartUnitPrice', cap(cartData.cartUnitPrice));
      void setCookieSync('cartCorePrice', cap(cartData.cartCorePrice));
      void setCookieSync('cartDiscountPriceList', cap(cartData.cartDiscountPriceList));
    }
    analyticsGlobalVariables();
  }, [cartData, locale]);

  /*  This effect controls the snack bar that notifies of changes in the selected vehicle
    and redirects to the selected vehicle when the user is in a vehicle detail page
  */
  useEffect(() => {
    if (justMounted.current) {
      justMounted.current = false;
      return;
    }
    const userWasLoggedIn = !isAnonymousUser && prevAnonymousStatus === 'true';

    const routesToIgnore =
      [routePaths.customerInfo, routePaths.checkout].includes(
        getPathnameFromAsPath(legacyRouter.asPath),
      ) || routePaths.resetPasswordPathName === legacyRouter.pathname;

    if (
      (vehicleId || vehicleName) &&
      !routesToIgnore &&
      (vehicleId !== prevVehicleId || acesFlags?.acesEnabled) &&
      prevHeaderDataUpdateAt &&
      localStorage.getItem('shouldDisplayLoginMsgOnReload') !== 'true' &&
      !userWasLoggedIn
    ) {
      if (
        legacyRouter.route === `${routePaths.vehicleDetails}[vehicleId]` &&
        parseInt(vehicleCount ?? '0') > parseInt(prevVehicleCount ?? '0')
      ) {
        void router.push(`${routePaths.vehicleDetails}${preferredVehicleData?.vehicleId}`);
      }

      openSnackBar(
        isMobileQRExperience
          ? `${lblScrollToSeeItemsThatFit} ${vehicleName}.`
          : `${lblShoppingFor} ${vehicleName}.`,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, vehicleName]);

  useEffect(() => {
    if (legacyRouter.asPath.includes('cmpid') && !isMobileQRExperience && vehicleId) {
      openSnackBar(`${lblShoppingFor} ${vehicleName}.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
    FIXME: This is a very hacky solution, but scrolling to the top immediately scrolls to the location of the sticky
    header, hence the timeout. This can be removed once the review writing UI is controlled by the FE.
  */
  useEffect(() => {
    const modal = document.querySelector('.bv-mbox');
    if (modal) {
      const closeButton = modal.querySelector<HTMLButtonElement>('.bv-mbox-close');
      if (closeButton) {
        closeButton.click();
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 500);
      }
    }
  }, [legacyRouter.asPath]);

  useEffect(() => {
    if (locale === countryCodes.ptBr) {
      return;
    }
    const isProtectedRoute = isRouteProtected(legacyRouter.asPath);

    if (
      !legacyRouter.asPath.includes('create') &&
      !legacyRouter.asPath.startsWith(routePaths.completeAccount) &&
      legacyRouter.asPath !== '/signin' &&
      legacyRouter.pathname !== '/signin' &&
      legacyRouter.asPath !== routePaths.createRewardsAccount
    ) {
      void setCookieSync('redirect_url', legacyRouter.asPath, { path: '/' });
    }

    if (!appData.userAuthenticated) {
      if (isProtectedRoute) {
        router.replace(routePaths.signIn);
      }
    }

    if (!appData.userAuthenticated && isProtectedRoute && canShowToastNotification && !rememberMe) {
      router.replace(routePaths.signIn);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyRouter.asPath]);

  const resetSnackBar = () => {
    setShowSnackBar(false);
  };

  const signedUserName = appData.userAuthenticated
    ? {
        userName,
      }
    : {};

  const currentPathIsCheckout = legacyRouter.asPath.includes(routePaths.checkout);
  const currentPathIsHome = legacyRouter.asPath === routePaths.blankURL;
  const currentPathIsCart = legacyRouter.asPath.includes(routePaths.cart);

  const headerPresent = appData.isHeaderPresent || appData.isSecondaryHeaderFooterPresent;
  const { url: currentUrl } = parseUrl(legacyRouter.asPath);
  const routerActualPath = legacyRouter.asPath || '';
  const splitRoute = routerActualPath ? routerActualPath?.split('/') : [];
  const isLandingPage = splitRoute[1] === 'lp';
  const isHomePage = currentUrl === routePaths.blankURL;
  const isCustomerInfoPage = legacyRouter.asPath === routePaths.customerInfo;
  let scripts;

  if (currentPathIsHome) {
    scripts = locale === countryCodes.us ? homePageMeta.app.scriptUS : homePageMeta.app.scriptMX;
  } else {
    scripts =
      locale === countryCodes.us ? config.app.mobileMeta.scriptUS : config.app.mobileMeta.scriptMX;
  }
  // Reset Password Toast Notification
  const canShowResetPasswordToastNotification =
    ![routePaths.signIn, routePaths.checkout].includes(legacyRouter.asPath.split('?')[0]) &&
    !isAnonymousUser;
  const handleCloseResetPasswordToastNotification = () => {
    sessionStorage.removeItem('resetPasswordFrom');
    setIsResetPasswordToastNotificationOpen(false);
  };
  useEffect(() => {
    const onResetPasswordAndSignIn = () => {
      if (sessionStorage.getItem('resetPasswordFrom') === 'forgotPassword') {
        setIsResetPasswordToastNotificationOpen(true);
      }
    };
    document.addEventListener('resetPasswordAndSignIn', onResetPasswordAndSignIn);
    return () => {
      document.removeEventListener('resetPasswordAndSignIn', onResetPasswordAndSignIn);
    };
  }, []);
  useEffect(() => {
    if (sessionStorage.getItem('resetPasswordFrom') === 'forgotPassword') {
      setIsResetPasswordToastNotificationOpen(true);
    }
  }, []);

  useResetSignInNotifications(setIsResetPasswordToastNotificationOpen);

  const displayOneTrust =
    oneTrustEnabled && !isABot && !isMobileApp
      ? oneTrustDomainScriptUrl && oneTrustScriptUrl
        ? true
        : false
      : false;

  const oneTrustOrigin = useMemo(() => {
    if (oneTrustEnabled && oneTrustScriptUrl && !oneTrustEnableNewBanner) {
      try {
        const url = new URL(oneTrustScriptUrl);
        return url.origin;
      } catch {
        logger.error({
          message: 'Error parsing OneTrust script URL',
          meta: { scriptURL: oneTrustScriptUrl },
        });
      }
    }
  }, [oneTrustEnabled, oneTrustScriptUrl, oneTrustEnableNewBanner]);

  function clearDuplicates(): void {
    const section = document.getElementById('ot-sdk-cookie-policy');
    const tally: { [key: string]: number } = {};
    if (section) {
      const children = Array.from(section.children);
      for (let i = children.length - 1; i >= 0; i--) {
        const childText = children[i].firstChild?.textContent;
        if (childText) {
          if (tally[childText] === undefined) {
            tally[childText] = 1;
          } else {
            section.removeChild(children[i]);
          }
        }
      }
    }
  }

  useEffect(() => {
    if (window.OneTrust != null && !isMobileApp && !oneTrustEnableNewBanner) {
      clearDuplicates();
      const attachHandlers = () => {
        const cookieSettingButtons = Array.from(
          document.getElementsByClassName('ot-sdk-show-settings'),
        ) as HTMLElement[];
        for (const btn of cookieSettingButtons) {
          if (
            typeof window.OneTrust !== 'undefined' &&
            typeof window.OneTrust.ToggleInfoDisplay === 'function'
          ) {
            btn.onclick = () => window.OneTrust?.ToggleInfoDisplay();
          }
        }
      };
      // Check if OneTrust is ready, otherwise poll
      if (window.OneTrust?.IsAlertBoxClosed !== undefined) {
        attachHandlers();
      } else {
        const checkInterval = setInterval(() => {
          if (window.OneTrust?.IsAlertBoxClosed !== undefined) {
            clearInterval(checkInterval);
            attachHandlers();
          }
        }, 100);

        // Cleanup after 5 seconds to prevent infinite polling
        setTimeout(() => clearInterval(checkInterval), 5000);
      }
    }
  }, [oneTrustEnableNewBanner, isMobileApp]);

  const enableBuyAgainOnPlpAndPdp = useForteDecisionFlagWithTrueDefault(
    'enableBuyAgainOnPlpAndPdp',
  );
  const buyAgainEndpointEnabled = useFeatureFlag('BUY_AGAIN_ENDPOINT_ENABLED') === 'true';
  const dateRange = Number(useFeatureFlag('BUY_AGAIN_DATE_RANGE'));

  const currentDate = new Date();
  const { fromDate, toDate } = getFormattedDateRange({
    date: currentDate,
    dateRange,
  });

  const { pageType } = usePageType();
  const isPLP =
    pageType === pageTypes.productShelf ||
    pageType === pageTypes.search ||
    pageType === pageTypes.BrandShelf ||
    pageType === pageTypes.MerchShelf;
  const isPDP = pageType === pageTypes.ProductDetail;
  const isAccountPage = pageType === pageTypes.myAccount;

  const showPLPBuyAgainCandidates =
    enableBuyAgainOnPlpAndPdp &&
    sessionStorage.getItem('showPLPBuyAgainCandidates') !== 'false' &&
    isPLP;
  const showPDPBuyAgainCandidates =
    enableBuyAgainOnPlpAndPdp &&
    sessionStorage.getItem('showPDPBuyAgainCandidates') !== 'false' &&
    isPDP;
  const showAccountBuyAgainCandidates =
    sessionStorage.getItem('showAccountBuyAgainCandidates') !== 'false' && isAccountPage;
  const showHomeBuyAgainCandidates =
    sessionStorage.getItem('showHomeBuyAgainCandidates') !== 'false' && isHomePage;

  const shouldCallPurchaseSummary =
    locale === countryCodes.us &&
    buyAgainEndpointEnabled &&
    (showPLPBuyAgainCandidates ||
      showPDPBuyAgainCandidates ||
      showAccountBuyAgainCandidates ||
      showHomeBuyAgainCandidates) &&
    userAuthenticated;

  useBuyAgainPurchases({
    fromDate,
    toDate,
    enabled: shouldCallPurchaseSummary,
  });

  return (
    <div
      className={
        !currentPathIsCheckout
          ? styles.App
          : checkoutVersionEnableV3_us || checkoutVersionEnableV3_mx
            ? styles.checkoutAppV3
            : styles.checkoutApp
      }
    >
      <Head>
        {displayOneTrust && (
          <>
            <link rel="preconnect" href={oneTrustOrigin} />
          </>
        )}
        <meta name="google-site-verification" content={googleSiteVerificationKey} />
      </Head>
      {displayOneTrust && (
        <>
          <Script
            id="onetrust-sdk"
            src={oneTrustScriptUrl}
            type="text/javascript"
            data-domain-script={oneTrustDomainScriptUrl}
            strategy="beforeInteractive"
          />
          <Script
            id="onetrust-optanon-wrapper"
            strategy="afterInteractive"
            type="text/javascript"
            src="/js/deleteCookies.js"
          />
        </>
      )}
      {customScripts?.map(({ c, async, ...script }, i) => {
        const strategy = async ? 'afterInteractive' : 'beforeInteractive';
        const id = `custom-script-${i}`;
        return c ? (
          <Script data-injected id={id} key={id} strategy={strategy} {...script}>
            {c}
          </Script>
        ) : (
          <Script data-injected id={id} key={id} strategy={strategy} {...script} />
        );
      })}

      {kiboEnabled && kiboScriptUrl && (
        <>
          <Script id="kibo-monetate-init" type="text/javascript" strategy="afterInteractive">
            {`var monetateT = new Date().getTime();`}
          </Script>
          <Script
            id="kibo-loader"
            type="text/javascript"
            src={kiboScriptUrl}
            strategy="afterInteractive"
          />
        </>
      )}
      {scripts.map((script, ind) => (
        <Script
          id={`app-script-${ind}`}
          type={script.type}
          key={`script-${ind}`}
          strategy="afterInteractive"
        >
          {script.innerHTML}
        </Script>
      ))}
      {displayOneTrust && oneTrustEnableNewBanner && showBanner && (
        <div className={cx(styles.bannerContainer, { [styles.visible]: showBannerAnimation })}>
          <OneTrustCookieBanner
            acceptAllText={lblCookieBannerAcceptAll || ''}
            rejectAllText={lblCookieBannerRejectAll || ''}
            managePreferencesText={lblCookieBannerManagePreferences || ''}
            headerText={lblCookieBannerHeader || ''}
            mainMessageText={lblCookieBannerMainMessage || ''}
            boldMessageText={lblCookieBannerBoldMessage || ''}
            learnMoreText={lblCookieBannerLearnMore || ''}
            privacyPolicyText={lblCookieBannerPrivacyPolicy || ''}
            privacyPolicyUrl={lblCookieBannerPrivacyPolicyUrl || ''}
            clickBelowText={lblCookieBannerClickBelow || ''}
            clickHereText={lblCookieBannerClickHere || ''}
            orText={lblCookieBannerOr || ''}
            cookiesText={lblCookieBannerCookies || ''}
            andText={lblCookieBannerAnd || ''}
            dotText={lblCookieBannerPeriod || ''}
            byUsingText={lblCookieBannerByUsing || ''}
            termsAndConditionsText={lblCookieBannerTermsAndConditions || ''}
            termsAndConditionsUrl={lblCookieBannerTermsAndConditionsUrl || ''}
          />
        </div>
      )}
      {isSmartAppBannerEnabled && !isAppFlyerEnabled && !isMobileQRExperience ? (
        <SmartAppBanner />
      ) : null}

      {appData.isHeaderPresent && <Header />}
      <CMSExperienceInjectorV2 />

      {appData.isSecondaryHeaderFooterPresent && <HeaderSecondary />}
      {contactUsModalEnabled &&
      !currentPathIsCheckout &&
      !isCustomerInfoPage &&
      !isMobileQRExperience &&
      locale !== countryCodes.ptBr ? (
        <>
          <NeedHelpTab onClick={openContactUsModal} />
          <ContactUsModal
            isContactUsModalOpen={isContactUsModalOpen}
            setIsContactUsModalOpen={setIsContactUsModalOpen}
          />
        </>
      ) : null}
      {emailOptInAlertEnabled ? <EmailSideTab /> : null}
      {!contactUsModalEnabled && appData.isChatAgentAvailable ? <ChatSlider /> : null}
      {/* {showResetPasswordMessage && canResetPasswordNotificationOpen && ( */}
      {isResetPasswordToastNotificationOpen && canShowResetPasswordToastNotification && (
        <ContentContainer type="simple" withPadding>
          <Grid container justify="space-around">
            <Grid item lg={8} md={12} xs={12}>
              <ResetPasswordMessageV2
                successData={{ userName: signedUserName.userName ?? '' }}
                onClose={handleCloseResetPasswordToastNotification}
                canOpen={isResetPasswordToastNotificationOpen}
              />
            </Grid>
          </Grid>
        </ContentContainer>
      )}

      <AZSnackBar
        open={showSnackBar}
        message={snackBarMessage}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        onClose={resetSnackBar}
      />
      {isBanner90Open && <LazyBanner90Day />}
      {isRewardsPilotProgramBannerOpen && <LazyRewardsPilotProgramBanner />}
      {isHomePage ||
      legacyRouter.asPath === routePaths.userMyAccount ||
      legacyRouter.asPath === routePaths.sms ? (
        <main
          className={cx({
            [styles.fullWidth]: headerPresent,
          })}
          role="main"
          data-testid={headerPresent ? 'webel-home-page-display' : ''}
          id={headerPresent ? 'webel-home-page-display' : ''}
        >
          {mainContentFocus}
          {children}
        </main>
      ) : (
        <main
          className={cx(
            headerPresent && !isLandingPage && !currentPathIsCheckout && azCommonStyles.mainContent,
            {
              [azCommonStyles.isCartPage]: currentPathIsCart,
            },
          )}
          role="main"
        >
          {mainContentFocus}
          {children}
        </main>
      )}

      {appData.isFooterPresent && <Footer />}

      {appData.isSecondaryHeaderFooterPresent && <FooterSecondary />}
      {appData.isChatAgentAvailable ? <ProactiveChat /> : null}

      {!isABot && (
        <>
          <FeatureFlag flag="TEALEAF_ENABLED">
            <Script type="text/javascript" src="/js/tealeaf_6_1.min.js" strategy="lazyOnload" />
          </FeatureFlag>
          <Utag />
          <UtagSync />
        </>
      )}
      <JScrambler />
      {performanceLCPMeasurementsEnabled && <LazyLCPMeasurements />}
      <ForteTargeting />
    </div>
  );
};

export default App;
